// src/contexts/AuthContext.js

/**
 * Global Authentication Context
 * Manages user authentication state across the app
 * Provides current user data and auth methods
 *
 * Architecture:
 * - Mock mode: Uses CURRENT_USER from mockUsers.js
 * - Real mode: Fetches user from backend after login/signup
 * - Same interface for both modes - just swap the implementation
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { CURRENT_USER } from "../data/mockUsers";
import { setAccessToken, clearAccessToken } from "../utils/tokenManager";
import { authAPI } from "../api/authApi";
import { devLog, devWarn, logError } from "../utils/logger";

/**
 * The Context API creates a provider-consumer relationship
 * Creating an empty container to hold auth data
 */
const AuthContext = createContext();

/**
 * localStorage key for session persistence
 * Tracks whether a user has logged in before (prevents unnecessary refresh attempts)
 */
const HAS_SESSION_KEY = "traces:hasSession";

/**
 * Provider component that wraps the app
 * Manages authentication state and provides auth methods
 */
export function AuthProvider({ children }) {
  /**
   * Current authenticated user
   * In mock mode: Set to CURRENT_USER on mount
   * In real mode: Set after successful login/signup API call
   */
  const [user, setUser] = useState(null);

  /**
   * Loading state for authentication operations
   */
  const [loading, setLoading] = useState(true);

  /**
   * Error state for authentication operations
   */
  const [error, setError] = useState(null);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = user !== null;

  /**
   * Initialize auth state
   *
   * Mock mode: Auto-login with CURRENT_USER
   * Real mode: Try to refresh token using httpOnly cookie
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        setLoading(true);

        // Check if we are in mock mode
        if (process.env.REACT_APP_USE_MOCK_API === "true") {
          // Mock mode: Auto-login with CURRENT_USER
          await new Promise((resolve) => setTimeout(resolve, 300));
          setUser(CURRENT_USER);
        } else {
          // Real mode: Try to refresh token using httpOnly cookie
          const hasSession = localStorage.getItem(HAS_SESSION_KEY);
          if (hasSession) {
            try {
              // Call getCurrentUser - if token is expired, httpClient interceptor
              /**
               * To have one place where token refreshes are done, call getCurrentUser
               *  If token is expired, httpClient interceptor will automatically refresh it and retry the reques.
               */
              const userResponse = await authAPI.getCurrentUser();

              // Transform backend user format to frontend format
              const backendUser = userResponse.data.user;
              const transformedUser = {
                id: backendUser._id,
                name: `${backendUser.firstName} ${backendUser.lastName}`,
                email: backendUser.email,
                avatar: backendUser.photo,
                emailVerified: backendUser.emailVerified || false,
                onboardingCompleted: backendUser.onboardingCompleted || false,
                role: backendUser.role || 'user',
                maxRange: backendUser.maxRange || 50,
              };

              setUser(transformedUser);
            } catch (error) {
              // Refresj failed or no valid session - user needs to login
              devLog("Auth: Session restoration failed, user needs to login");
              clearAccessToken();
              localStorage.removeItem(HAS_SESSION_KEY);
              setUser(null);
            }
          } else {
            devLog("Auth: No previous session, user needs to login");
            setUser(null);
          }
        }
      } catch (error) {
        logError("Auth initialization error:", error);
        setError(error.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Verify email with code
   * Sets access token and user after successful verification
   */
  const verifyEmail = useCallback(async (code) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.verifyCode(code);
      setAccessToken(response.data.accessToken);

      // Transform backend user format to frontend format
      const backendUser = response.data.user;
      const transformedUser = {
        id: backendUser._id,
        name: `${backendUser.firstName} ${backendUser.lastName}`,
        email: backendUser.email,
        avatar: backendUser.photo,
        emailVerified: backendUser.emailVerified || false,
        onboardingCompleted: backendUser.onboardingCompleted || false,
        role: backendUser.role || 'user',
        maxRange: backendUser.maxRange || 50,
      };

      setUser(transformedUser);
      localStorage.setItem(HAS_SESSION_KEY, "true");
      return { success: true };
    } catch (error) {
      logError("Verification error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login function
   *
   * Mock mode: Just sets CURRENT_USER
   * Real mode: Calls backend login API, stores token, sets user
   */
  const login = useCallback(async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      // Check if we are in mock mode
      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: simulate login
        await new Promise((resolve) => setTimeout(resolve, 500));
        setUser(CURRENT_USER);
        localStorage.setItem(HAS_SESSION_KEY, "true");
      } else {
        // Real mode: Call backend API
        const response = await authAPI.login(credentials);
        devLog("AuthContext: Login response:", response.data);
        setAccessToken(response.data.accessToken);

        // Transform backend user format to frontend format
        const backendUser = response.data.user;
        const transformedUser = {
          id: backendUser._id,
          name: `${backendUser.firstName} ${backendUser.lastName}`,
          email: backendUser.email,
          avatar: backendUser.photo,
          emailVerified: backendUser.emailVerified || false,
          onboardingCompleted: backendUser.onboardingCompleted || false,
          role: backendUser.role || 'user',
          maxRange: backendUser.maxRange || 50,
        };

        devLog("AuthContext: Setting user to:", transformedUser);
        setUser(transformedUser);
        // Mark that a session exists (for next page reload)
        localStorage.setItem(HAS_SESSION_KEY, "true");
      }

      return { success: true };
    } catch (error) {
      logError("Login error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Signup function
   *
   * Mock mode: Just sets CURRENT_USER
   * Real mode: Calls backend signup API, stores token, sets user
   */
  const signup = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      // Check if we are in mock mode
      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: Simulate signup
        await new Promise((resolve) => setTimeout(resolve, 500));
        setUser(CURRENT_USER);
        return { success: true };
      } else {
        // Real mode: Call backend API
        const response = await authAPI.signup(userData);
        // Don't set user yet, verification required
        return {
          success: true,
          email: userData.email,
          message: response.data.message,
        };
      }
    } catch (error) {
      logError("Signup error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout function
   *
   * Mock mode: Just clears user state
   * Real mode: Calls backend logout API, removes token, clears user
   */
  const logout = useCallback(async () => {
    try {
      setLoading(true);

      // Check if we're in mock mode
      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: Simulate logout
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        // Real mode: Call backend API
        try {
          await authAPI.logout();
        } catch (error) {
          devWarn("Logout API call failed:", error);
          // Continue with local logout even if API fails
        }
      }

      clearAccessToken();
      localStorage.removeItem(HAS_SESSION_KEY);
      setUser(null);
      setError(null);
    } catch (err) {
      logError("Logout error:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update user profile
   *
   * Mock mode: Updates local user state
   * Real mode: Calls backend update API, updates user
   */
  const updateUser = useCallback(async (updates) => {
    try {
      setLoading(true);
      setError(null);

      // Check if we are in mock mode
      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: Update user locally
        await new Promise((resolve) => setTimeout(resolve, 300));
        setUser((prev) => ({ ...prev, ...updates }));
      } else {
        // Real mode: Call backend API
        const response = await authAPI.updateProfile(updates);

        // Transform backend user format to frontend format
        const backendUser = response.data.user;
        const transformedUser = {
          id: backendUser._id,
          name: `${backendUser.firstName} ${backendUser.lastName}`,
          email: backendUser.email,
          avatar: backendUser.photo,
          emailVerified: backendUser.emailVerified || false,
          onboardingCompleted: backendUser.onboardingCompleted || false,
          role: backendUser.role || 'user',
          maxRange: backendUser.maxRange || 50,
        };

        setUser(transformedUser);
      }

      return { success: true };
    } catch (error) {
      logError("Update user error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request password reset email
   *
   * Mock mode: Always succeeds (simulates email sent)
   * Real mode: Calls backend forgot-password API
   */
  const forgotPassword = useCallback(async (email) => {
    try {
      setError(null);

      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: Simulate sending email
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: true, message: "Token sent to email" };
      } else {
        // Real mode: Call backend API
        const response = await authAPI.forgotPassword(email);
        return { success: true, message: response.data.message };
      }
    } catch (error) {
      logError("Forgot password error:", error);
      throw error;
    }
  }, []);

  /**
   * Reset password using token from email
   *
   * Mock mode: Always succeeds, auto-logs in with CURRENT_USER
   * Real mode: Calls backend reset-password API, sets tokens, logs in user
   */
  const resetPassword = useCallback(async (token, password, passwordConfirm) => {
    try {
      setLoading(true);
      setError(null);

      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        // Mock mode: Simulate reset and auto-login
        await new Promise((resolve) => setTimeout(resolve, 500));
        setUser(CURRENT_USER);
        localStorage.setItem(HAS_SESSION_KEY, "true");
      } else {
        // Real mode: Call backend API
        const response = await authAPI.resetPassword(token, password, passwordConfirm);
        devLog("AuthContext: Reset password response:", response.data);
        setAccessToken(response.data.accessToken);

        // Transform backend user format to frontend format
        const backendUser = response.data.user;
        const transformedUser = {
          id: backendUser._id,
          name: `${backendUser.firstName} ${backendUser.lastName}`,
          email: backendUser.email,
          avatar: backendUser.photo,
          emailVerified: backendUser.emailVerified || false,
          onboardingCompleted: backendUser.onboardingCompleted || false,
          role: backendUser.role || 'user',
          maxRange: backendUser.maxRange || 50,
        };

        devLog("AuthContext: Setting user to:", transformedUser);
        setUser(transformedUser);
        localStorage.setItem(HAS_SESSION_KEY, "true");
      }

      return { success: true };
    } catch (error) {
      logError("Reset password error:", error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Sync user state locally without an API call.
   */
  const syncUser = useCallback((updates) => {
    setUser((prev) => ({ ...prev, ...updates }));
  }, []);

  /**
   * Check if user is an admin
   */
  const isAdmin = user?.role === 'admin';

  /**
   * Values available to all children components
   */
  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    signup,
    verifyEmail,
    forgotPassword,
    resetPassword,
    updateUser,
    syncUser,
    setError,
  };

  /**
   * Provide auth state to all children
   */
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to access auth context
 * Use this in any component that needs user data or auth methods
 *
 * Example usage:
 * const { user, login, logout, isAuthenticated } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
